/*
 * BATFIT — Vidéos toujours dans la page, jamais en plein écran.
 * Utilisé par snippets/bf-home-ugc.liquid, snippets/bf-aegis-videos.liquid et sections/bf-video.liquid.
 *
 * 1. Chaque vidéo reçoit les attributs « lecture dans la page » (Safari, Chrome, navigateurs intégrés
 *    TikTok / Instagram / Facebook / Snapchat, WeChat…) et perd tout accès au plein écran
 *    (pas de commandes natives, pas d'image dans l'image, requestFullscreen neutralisé).
 * 2. Navigateurs des applis sur iPhone (TikTok, Instagram, Facebook… détectés par snippets/bf-video-garde.liquid,
 *    29/10/2026) : la lecture image par image démarre directement, la vidéo n'est jamais lancée en lecture normale,
 *    donc jamais en plein écran, même une fraction de seconde.
 * 3. Si un autre navigateur force malgré tout le plein écran (certaines webviews d'apps sur iPhone),
 *    on en sort tout de suite et la vidéo continue dans la page, image par image, le son passant
 *    par un lecteur audio séparé. Le choix est retenu pour la session : les lectures suivantes
 *    démarrent directement dans ce mode, sans flash plein écran.
 *
 * API : window.BFInline.prepare(video), .play(video, { sound }), .pause(video),
 *       .seek(video, secondes), .isPlaying(video), .isMuted(video), .unmute(video).
 * Chaque changement d'état déclenche l'événement « bf:state » sur la vidéo.
 * Les scripts inline qui s'exécutent avant ce fichier (chargé en defer) passent par la file
 * window.bfInlineQ : (window.bfInlineQ = window.bfInlineQ || []).push(function (api) { ... });
 */
(function () {
  'use strict';

  if (window.BFInline) return;

  var KEY = 'bfNoInline';
  var forced = false;

  try {
    forced = window.sessionStorage.getItem(KEY) === '1';
  } catch (e) {}

  /* navigateur d'une appli sur iPhone : image par image d'office (détection dans snippets/bf-video-garde.liquid) */
  var ua = navigator.userAgent || '';
  var appli = window.BF_APPLI_IPHONE;
  if (appli === undefined) {
    appli = /iPhone|iPod|iPad/.test(ua) &&
      (/TikTok|musical_ly|Bytedance|trill_|Instagram|FBAN|FBAV|FB_IAB|FBIOS|Messenger|Snapchat|Pinterest|LinkedInApp|Twitter/i.test(ua) || !/Safari\//.test(ua));
  }
  if (appli) forced = true;

  var FS_METHODS = [
    'requestFullscreen',
    'webkitRequestFullscreen',
    'webkitRequestFullScreen',
    'mozRequestFullScreen',
    'msRequestFullscreen',
    'webkitEnterFullscreen',
    'webkitEnterFullScreen'
  ];

  function noop() {}

  function resolved() {
    return window.Promise ? Promise.resolve() : undefined;
  }

  function emit(video) {
    var event;
    try {
      event = new CustomEvent('bf:state');
    } catch (e) {
      event = document.createEvent('Event');
      event.initEvent('bf:state', false, false);
    }
    video.dispatchEvent(event);
  }

  function rememberForced() {
    forced = true;
    try {
      window.sessionStorage.setItem(KEY, '1');
    } catch (e) {}
  }

  function sourceOf(video) {
    if (video.currentSrc) return video.currentSrc;
    if (video.src) return video.src;
    var source = video.querySelector('source[src]');
    return source ? source.src : '';
  }

  /* ---------- Sortie immédiate du plein écran ---------- */

  function exitFullscreen(video) {
    try {
      if (video && video.webkitDisplayingFullscreen && video.webkitExitFullscreen) {
        video.webkitExitFullscreen();
      }
    } catch (e) {}

    var doc = document;
    if (doc.fullscreenElement || doc.webkitFullscreenElement) {
      try {
        var exit = doc.exitFullscreen || doc.webkitExitFullscreen || doc.webkitCancelFullScreen;
        var result = exit ? exit.call(doc) : null;
        if (result && result.catch) result.catch(noop);
      } catch (e) {}
    }
  }

  function onDocumentFullscreen() {
    var element = document.fullscreenElement || document.webkitFullscreenElement;
    if (element && element.tagName === 'VIDEO') exitFullscreen(element);
  }

  document.addEventListener('fullscreenchange', onDocumentFullscreen);
  document.addEventListener('webkitfullscreenchange', onDocumentFullscreen);

  /* ---------- Lecture image par image (secours) ---------- */

  function frameState(video) {
    if (!video.__bfFrame) {
      video.__bfFrame = { playing: false, muted: true, audio: null, clock: 0, last: 0, raf: 0 };
    }
    return video.__bfFrame;
  }

  function tick(video, state, now) {
    if (!state.playing) return;

    var delta = state.last ? Math.min((now - state.last) / 1000, 0.25) : 0;
    state.last = now;

    var audio = state.audio;
    if (audio && !state.muted && !audio.paused && audio.readyState >= 2) {
      state.clock = audio.currentTime;
    } else if (video.readyState >= 1) {
      /* la vitesse choisie (bouton ×1,5 / ×2 de sections/bf-video.liquid, 01/11/2026) vaut aussi image par image */
      state.clock += delta * (video.playbackRate || 1);
    }

    var duration = video.duration;
    if (duration && isFinite(duration) && state.clock >= duration) {
      if (video.loop) {
        state.clock = state.clock % duration;
      } else {
        state.clock = duration;
        stopFrame(video);
        return;
      }
    }

    if (video.readyState >= 1 && !video.seeking && Math.abs(video.currentTime - state.clock) > 0.015) {
      try {
        video.currentTime = state.clock;
      } catch (e) {}
    }

    state.raf = requestAnimationFrame(function (next) {
      tick(video, state, next);
    });
  }

  function startFrame(video, time, sound) {
    var state = frameState(video);

    if (video.preload === 'none') video.preload = 'auto';
    if (video.readyState === 0) {
      try {
        video.load();
      } catch (e) {}
    }

    state.clock = time || video.currentTime || 0;
    state.last = 0;
    state.playing = true;
    state.muted = true;

    cancelAnimationFrame(state.raf);
    state.raf = requestAnimationFrame(function (now) {
      tick(video, state, now);
    });

    if (sound) unmute(video);
    emit(video);
  }

  function stopFrame(video) {
    var state = video.__bfFrame;
    if (!state) return;
    state.playing = false;
    cancelAnimationFrame(state.raf);
    if (state.audio) {
      try {
        state.audio.pause();
      } catch (e) {}
    }
    emit(video);
  }

  function unmute(video) {
    var state = video.__bfFrame;

    if (!state || !state.playing) {
      video.muted = false;
      return resolved();
    }

    if (!state.audio) {
      var audio = new Audio();
      audio.preload = 'auto';
      audio.loop = video.loop;
      audio.src = sourceOf(video);
      audio.addEventListener('loadedmetadata', function () {
        try {
          audio.currentTime = state.clock;
        } catch (e) {}
      });
      state.audio = audio;
    }

    try {
      state.audio.currentTime = state.clock;
      state.audio.playbackRate = video.playbackRate || 1;
    } catch (e) {}

    state.muted = false;

    var promise;
    try {
      promise = state.audio.play();
    } catch (e) {
      state.muted = true;
    }

    if (promise && promise.then) {
      promise.then(
        function () {
          emit(video);
        },
        function () {
          /* Son refusé sans nouveau toucher : la vidéo continue sans le son. */
          state.muted = true;
          emit(video);
        }
      );
    }

    emit(video);
    return promise || resolved();
  }

  /* ---------- Plein écran imposé par le navigateur ---------- */

  function resume(video) {
    var pending = video.__bfResume;
    if (!pending) return;
    video.__bfResume = null;

    try {
      video.pause();
    } catch (e) {}

    startFrame(video, pending.time, pending.sound);
    video.__bfSwitching = false;
    emit(video);
  }

  function takeover(video) {
    rememberForced();

    video.__bfSwitching = true;
    video.__bfResume = { time: video.currentTime || 0, sound: !video.muted };

    exitFullscreen(video);

    [60, 250, 600].forEach(function (delay) {
      setTimeout(function () {
        if (video.webkitDisplayingFullscreen) exitFullscreen(video);
      }, delay);
    });

    setTimeout(function () {
      resume(video);
    }, 700);
  }

  /* ---------- API ---------- */

  function prepare(video) {
    if (!video || video.__bfInline) return video;
    video.__bfInline = true;

    video.controls = false;
    video.removeAttribute('controls');
    video.playsInline = true;
    video.setAttribute('playsinline', '');
    video.setAttribute('webkit-playsinline', '');
    video.setAttribute('x5-playsinline', '');
    video.setAttribute('x5-video-player-type', 'h5-page');
    video.setAttribute('x5-video-player-fullscreen', 'false');
    video.setAttribute('disablepictureinpicture', '');
    video.setAttribute('disableremoteplayback', '');
    video.setAttribute('controlslist', 'nofullscreen nodownload noremoteplayback noplaybackrate');

    try {
      video.disablePictureInPicture = true;
    } catch (e) {}
    try {
      video.disableRemotePlayback = true;
    } catch (e) {}

    FS_METHODS.forEach(function (method) {
      if (typeof video[method] === 'function') {
        try {
          video[method] = resolved;
        } catch (e) {}
      }
    });

    video.addEventListener('webkitbeginfullscreen', function () {
      takeover(video);
    });

    video.addEventListener('webkitendfullscreen', function () {
      if (video.__bfResume) resume(video);
    });

    video.addEventListener('webkitpresentationmodechanged', function () {
      if (video.webkitPresentationMode && video.webkitPresentationMode !== 'inline' && video.webkitSetPresentationMode) {
        try {
          video.webkitSetPresentationMode('inline');
        } catch (e) {}
      }
    });

    video.addEventListener('enterpictureinpicture', function () {
      if (document.exitPictureInPicture) document.exitPictureInPicture().catch(noop);
    });

    video.addEventListener('dblclick', function (event) {
      event.preventDefault();
      event.stopPropagation();
    });

    ['play', 'playing', 'pause', 'ended'].forEach(function (type) {
      video.addEventListener(type, function () {
        emit(video);
      });
    });

    return video;
  }

  function play(video, options) {
    prepare(video);

    var sound = !!(options && options.sound);

    if (forced) {
      try {
        video.pause();
      } catch (e) {}
      startFrame(video, video.currentTime, sound);
      return resolved();
    }

    video.muted = !sound;

    var promise;
    try {
      promise = window.__bfLectureNative ? window.__bfLectureNative.call(video) : video.play();
    } catch (e) {
      return window.Promise ? Promise.reject(e) : undefined;
    }

    if (!promise || !promise.then) return resolved();

    return promise.catch(function (error) {
      if (video.__bfSwitching || (video.__bfFrame && video.__bfFrame.playing)) return;
      throw error;
    });
  }

  function pause(video) {
    video.__bfResume = null;
    video.__bfSwitching = false;
    if (video.__bfFrame && video.__bfFrame.playing) stopFrame(video);
    try {
      video.pause();
    } catch (e) {}
  }

  /* Avancer / reculer à la main (barre glissée au doigt), aussi en lecture image par image */
  function seek(video, time) {
    var duration = video.duration;
    if (!isFinite(time)) return;
    if (duration && isFinite(duration)) time = Math.min(Math.max(time, 0), Math.max(duration - 0.05, 0));
    else time = Math.max(time, 0);

    var state = video.__bfFrame;
    if (state) {
      state.clock = time;
      state.last = 0;
      if (state.audio) {
        try {
          state.audio.currentTime = time;
        } catch (e) {}
      }
    }

    try {
      video.currentTime = time;
    } catch (e) {}
  }

  function isPlaying(video) {
    if (video.__bfSwitching) return true;
    if (video.__bfFrame && video.__bfFrame.playing) return true;
    return !video.paused && !video.ended;
  }

  function isMuted(video) {
    var state = video.__bfFrame;
    return !!(state && state.playing && state.muted);
  }

  var api = {
    prepare: prepare,
    play: play,
    pause: pause,
    seek: seek,
    isPlaying: isPlaying,
    isMuted: isMuted,
    unmute: unmute,
    stop: stopFrame,
    appli: appli
  };

  window.BFInline = api;

  var queue = window.bfInlineQ;
  window.bfInlineQ = {
    push: function (callback) {
      try {
        callback(api);
      } catch (e) {
        console.warn('BFInline :', e);
      }
    }
  };

  if (queue && queue.length) {
    for (var i = 0; i < queue.length; i++) window.bfInlineQ.push(queue[i]);
  }
})();
